export type CibInline =
  | { kind: "text"; text: string }
  | { kind: "strong"; text: string }
  | { kind: "emphasis"; text: string }
  | { kind: "code"; text: string }
  | { kind: "link"; text: string; href: string };

export type CibBlock =
  | { kind: "heading"; level: number; id: string; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "list"; ordered: boolean; items: string[] }
  | { kind: "table"; headers: string[]; rows: string[][] }
  | { kind: "quote"; text: string }
  | { kind: "divider" };

export interface CibOutlineItem {
  id: string;
  level: number;
  text: string;
}

const HEADING = /^(#{1,6})\s+(.+?)\s*$/;
const UNORDERED_ITEM = /^\s*[-+*]\s+(.+?)\s*$/;
const ORDERED_ITEM = /^\s*\d+[.)]\s+(.+?)\s*$/;
const DIVIDER = /^\s{0,3}(?:(?:-\s*){3,}|(?:_\s*){3,}|(?:\*\s*){3,})$/;
const TABLE_DIVIDER_CELL = /^:?-{3,}:?$/;

function isSafeExternalUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}

function trimUrlPunctuation(value: string): string {
  return value.replace(/[.,;:!?]+$/, "");
}

/**
 * A deliberately small, non-HTML inline grammar for generated CIB content.
 * It never evaluates provider HTML and only creates links for HTTP(S) URLs.
 */
export function parseCibInline(value: string): CibInline[] {
  const tokens: CibInline[] = [];
  const matcher =
    /(\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*]+)\*\*|__([^_]+)__|`([^`]+)`|\*([^*\n]+)\*|_([^_\n]+)_|https?:\/\/[^\s<]+)/g;
  let cursor = 0;

  for (const match of value.matchAll(matcher)) {
    const index = match.index ?? 0;
    if (index > cursor) {
      tokens.push({ kind: "text", text: value.slice(cursor, index) });
    }

    if (match[2] && match[3] && isSafeExternalUrl(match[3])) {
      tokens.push({ kind: "link", text: match[2], href: match[3] });
    } else if (match[4] || match[5]) {
      tokens.push({ kind: "strong", text: match[4] ?? match[5] ?? "" });
    } else if (match[6]) {
      tokens.push({ kind: "code", text: match[6] });
    } else if (match[7] || match[8]) {
      tokens.push({ kind: "emphasis", text: match[7] ?? match[8] ?? "" });
    } else {
      const href = trimUrlPunctuation(match[0]);
      if (isSafeExternalUrl(href)) {
        tokens.push({ kind: "link", text: href, href });
        const suffix = match[0].slice(href.length);
        if (suffix) tokens.push({ kind: "text", text: suffix });
      } else {
        tokens.push({ kind: "text", text: match[0] });
      }
    }
    cursor = index + match[0].length;
  }

  if (cursor < value.length) {
    tokens.push({ kind: "text", text: value.slice(cursor) });
  }
  return tokens.length > 0 ? tokens : [{ kind: "text", text: value }];
}

function splitTableRow(line: string): string[] {
  const source = line.trim().replace(/^\|/, "").replace(/\|$/, "");
  const cells: string[] = [];
  let current = "";
  let escaped = false;

  for (const character of source) {
    if (escaped) {
      current += character;
      escaped = false;
    } else if (character === "\\") {
      escaped = true;
    } else if (character === "|") {
      cells.push(current.trim());
      current = "";
    } else {
      current += character;
    }
  }
  if (escaped) current += "\\";
  cells.push(current.trim());
  return cells;
}

function isTableStart(lines: string[], index: number): boolean {
  if (!lines[index]?.includes("|") || !lines[index + 1]?.includes("|")) return false;
  const header = splitTableRow(lines[index] ?? "");
  const divider = splitTableRow(lines[index + 1] ?? "");
  return (
    header.length > 0 &&
    header.length === divider.length &&
    divider.every((cell) => TABLE_DIVIDER_CELL.test(cell.replace(/\s/g, "")))
  );
}

function isBlockStart(lines: string[], index: number): boolean {
  const line = lines[index] ?? "";
  return (
    line.trim() === "" ||
    HEADING.test(line) ||
    DIVIDER.test(line) ||
    UNORDERED_ITEM.test(line) ||
    ORDERED_ITEM.test(line) ||
    /^\s*>/.test(line) ||
    isTableStart(lines, index)
  );
}

function slug(value: string): string {
  const normalized = value
    .toLowerCase()
    .replace(/[`*_#[\]()]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return normalized || "section";
}

function uniqueHeadingId(text: string, seen: Map<string, number>): string {
  const base = `cib-${slug(text)}`;
  const count = (seen.get(base) ?? 0) + 1;
  seen.set(base, count);
  return count === 1 ? base : `${base}-${count}`;
}

/** Parse the provider's Markdown-shaped body without accepting raw HTML. */
export function parseCibMarkdown(markdown: string): CibBlock[] {
  const lines = markdown.replace(/\r\n?/g, "\n").split("\n");
  const blocks: CibBlock[] = [];
  const headingIds = new Map<string, number>();
  let index = 0;

  while (index < lines.length) {
    const line = lines[index] ?? "";
    if (!line.trim()) {
      index += 1;
      continue;
    }

    const heading = line.match(HEADING);
    if (heading?.[1] && heading[2]) {
      blocks.push({
        kind: "heading",
        level: heading[1].length,
        id: uniqueHeadingId(heading[2], headingIds),
        text: heading[2],
      });
      index += 1;
      continue;
    }

    if (DIVIDER.test(line)) {
      blocks.push({ kind: "divider" });
      index += 1;
      continue;
    }

    if (isTableStart(lines, index)) {
      const headers = splitTableRow(line);
      const rows: string[][] = [];
      index += 2;
      while (index < lines.length && (lines[index] ?? "").includes("|")) {
        const row = splitTableRow(lines[index] ?? "");
        rows.push(headers.map((_, cellIndex) => row[cellIndex] ?? ""));
        index += 1;
      }
      blocks.push({ kind: "table", headers, rows });
      continue;
    }

    const unordered = line.match(UNORDERED_ITEM);
    const ordered = line.match(ORDERED_ITEM);
    if (unordered?.[1] || ordered?.[1]) {
      const orderedList = Boolean(ordered?.[1]);
      const pattern = orderedList ? ORDERED_ITEM : UNORDERED_ITEM;
      const items: string[] = [];
      while (index < lines.length) {
        const item = (lines[index] ?? "").match(pattern);
        if (!item?.[1]) break;
        items.push(item[1]);
        index += 1;
      }
      blocks.push({ kind: "list", ordered: orderedList, items });
      continue;
    }

    if (/^\s*>/.test(line)) {
      const quoteLines: string[] = [];
      while (index < lines.length && /^\s*>/.test(lines[index] ?? "")) {
        quoteLines.push((lines[index] ?? "").replace(/^\s*>\s?/, ""));
        index += 1;
      }
      blocks.push({ kind: "quote", text: quoteLines.join(" ").trim() });
      continue;
    }

    const paragraph: string[] = [];
    while (index < lines.length && !isBlockStart(lines, index)) {
      paragraph.push((lines[index] ?? "").trim());
      index += 1;
    }
    if (paragraph.length > 0) {
      blocks.push({ kind: "paragraph", text: paragraph.join(" ") });
    } else {
      // A defensive progress step for an unrecognised construct.
      blocks.push({ kind: "paragraph", text: line.trim() });
      index += 1;
    }
  }

  return blocks;
}

export function cibOutline(blocks: CibBlock[]): CibOutlineItem[] {
  return blocks
    .filter(
      (block): block is Extract<CibBlock, { kind: "heading" }> =>
        block.kind === "heading" && block.level >= 2 && block.level <= 3
    )
    .map(({ id, level, text }) => ({ id, level, text }));
}
